import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Cookie } from '@phosphor-icons/react';

const KEY = 'dashlink_cookie_consent';
const OPEN_EVENT = 'dashlink:open-cookie-settings';
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_EVENT));

const readConsent = () => {
  try {
    const c = JSON.parse(localStorage.getItem(KEY) || 'null');
    return c && Date.now() - c.savedAt < MAX_AGE_MS ? c : null;
  } catch {
    return null;
  }
};

const applyConsent = ({ analytics, ads }) => {
  const v = (on) => (on ? 'granted' : 'denied');
  window.gtag?.('consent', 'update', {
    ad_storage: v(ads), ad_user_data: v(ads), ad_personalization: v(ads), analytics_storage: v(analytics),
  });
  const ph = window.posthog;
  if (ph?.opt_in_capturing) analytics ? ph.opt_in_capturing() : ph.opt_out_capturing();
};

const saveConsent = (choice) => {
  const record = { ...choice, savedAt: Date.now() };
  localStorage.setItem(KEY, JSON.stringify(record));
  applyConsent(record);
};

const gpcOn = () => typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;

const Toggle = ({ id, label, hint, checked, onChange, disabled }) => (
  <label htmlFor={id} className="flex items-start justify-between gap-4 py-3 border-t border-[#E4E4E7]">
    <span>
      <span className="block text-sm font-medium text-[#09090B]">{label}</span>
      <span className="block text-xs text-[#71717A] mt-0.5">{hint}</span>
    </span>
    <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={`cookie-toggle-${id}`} />
  </label>
);

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, ads: false });

  useEffect(() => {
    const existing = readConsent();
    if (existing) applyConsent(existing);
    else setOpen(true);
    const reopen = () => {
      const c = readConsent();
      setPrefs({ analytics: !!c?.analytics, ads: !!c?.ads });
      setCustom(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  const decide = (choice) => {
    saveConsent({ analytics: choice.analytics, ads: choice.ads && !gpcOn() });
    setOpen(false);
    setCustom(false);
  };

  if (!open) return null;

  return (
    <div role="dialog" aria-live="polite" aria-label="Cookie consent" data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60] bg-white border border-[#E4E4E7] shadow-2xl p-6 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="flex items-center gap-2 mb-2">
        <Cookie size={20} weight="fill" className="text-[#0047FF]" />
        <p className="font-heading font-bold text-[#09090B]">Your privacy, your choice</p>
      </div>
      <p className="text-sm text-[#71717A] leading-relaxed">
        We use essential cookies to keep you signed in. With your permission we&apos;d also like to use analytics cookies to improve
        DashLink and advertising cookies (Google AdSense) to keep it free. See our{' '}
        <Link to="/cookies" className="text-[#0047FF] underline" data-testid="cookie-policy-link">Cookie Policy</Link>.
      </p>
      {custom && (
        <div className="mt-4" data-testid="cookie-preferences">
          <Toggle id="necessary" label="Strictly necessary" hint="Sign-in and security. Always on." checked disabled onChange={() => {}} />
          <Toggle id="analytics" label="Analytics" hint="Helps us understand which features are used." checked={prefs.analytics}
            onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} />
          <Toggle id="ads" label="Advertising" hint={gpcOn() ? 'Disabled by your browser’s Global Privacy Control signal.' : 'Lets Google show and measure ads, including personalised ads.'}
            checked={prefs.ads && !gpcOn()} disabled={gpcOn()} onChange={(v) => setPrefs((p) => ({ ...p, ads: v }))} />
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        {custom ? (
          <Button onClick={() => decide(prefs)} className="bg-[#0047FF] hover:bg-[#0037CC] text-white" data-testid="cookie-save">Save choices</Button>
        ) : (
          <Button variant="outline" onClick={() => setCustom(true)} className="border-[#E4E4E7]" data-testid="cookie-customize">Customize</Button>
        )}
        <Button variant="outline" onClick={() => decide({ analytics: false, ads: false })} className="border-[#E4E4E7]" data-testid="cookie-reject">Reject non-essential</Button>
        <Button onClick={() => decide({ analytics: true, ads: true })} className="bg-[#09090B] hover:bg-black text-white" data-testid="cookie-accept">Accept all</Button>
      </div>
    </div>
  );
};
